import { yup } from '@framework/validation/initYup.ts';
import { type YupAnyObjectSchema } from '@framework/validation/YupObjectSchema.ts';
import { fromPairs, get } from 'lodash';
import { FormBuilderMetaFlags } from '../../../../../../validator/form-builder/flags/FormBuilderMetaFlags.ts';
import { getMaterializedExpressionReferences } from '../../../../../expressions/evaluate/getMaterializedExpressionReferences.ts';
import { type ICreateWidgetFormSchemaInput } from '../../../../IWidgetType.ts';
import { getDatatypeOutputSchema } from '../../../common/async-widgets/getDatatypeOutputSchema.ts';
import { asyncValueWidgetObjectName } from '../AsyncValueWidget.type.tsx';
import { type IAsyncValueWidget } from '../IAsyncValueWidget.ts';
import { getServerSideValidationSchema } from './getServerSideValidationSchema.ts';

export function createAsyncValueFormSchema(
  input: ICreateWidgetFormSchemaInput<IAsyncValueWidget>,
) {
  const { widget } = input;

  const outputs = widget.outputs ?? [];

  const objectSchema = yup
    .object(
      fromPairs(
        outputs.map(output => [
          output.name,
          getDatatypeOutputSchema(output.type),
        ]),
      ),
    )
    .formBuilderFlags(
      FormBuilderMetaFlags.SkipDefaultValue,
      FormBuilderMetaFlags.SkipValidationRules,
    )
    .noUnknown()
    .notRequired();

  let asyncValueSchema = objectSchema;

  // Se è abilitata la validazione server-side, aggiungo la regola di validazione
  if (widget.enableServerSideValidation) {
    const materializedReferences = getMaterializedExpressionReferences({
      name: widget.code,
      expression: Object.values(widget.inputs || {}),
    });

    asyncValueSchema = getServerSideValidationSchema(asyncValueSchema, {
      widget,
      materializedReferences,
      objectSchema: objectSchema as YupAnyObjectSchema,
    }) as YupAnyObjectSchema;
  }

  let widgetSchema = yup.string().notRequired();

  if (widget.enableServerSideValidation) {
    // In caso di validazione server-side, il valore del widget viene
    // preso dall'oggetto materializzato (quindi dal risultato del servizio)
    widgetSchema = widgetSchema.when(
      ['$trustedEnvironment', asyncValueWidgetObjectName(widget.code)],
      (values, schema) => {
        const trustedEnvironment: boolean = values[0];

        if (!trustedEnvironment) return schema;

        const objectValue = values[1];

        return schema.transform(() => get(objectValue, widget.valueField));
      },
    );
  }

  return {
    [widget.code]: widgetSchema,
    [asyncValueWidgetObjectName(widget.code)]: asyncValueSchema,
  };
}
